import { useState } from "react";
import { useConnect, useConnection, useConnectors, useDisconnect, useSwitchChain } from "wagmi";
import { robinhood } from "../lib/config";
import { shortAddr } from "../lib/format";
import { friendlyError } from "../lib/errors";
import { connectDemo, DEMO_ADDRESS, useDemoWallet, useMode } from "../lib/demo";
import { Modal } from "./Modal";

export function ConnectButton({ block = false }: { block?: boolean }) {
  const mode = useMode();
  return mode === "demo" ? <DemoConnect block={block} /> : <WalletConnect block={block} />;
}

/** Preview mode: a demo wallet with a few positions, so every flow can be tried safely. */
function DemoConnect({ block }: { block: boolean }) {
  const on = useDemoWallet();
  const [open, setOpen] = useState(false);
  if (on) {
    return (
      <button className={`btn ${block ? "btn-block" : ""}`} onClick={() => connectDemo(false)} title="Disconnect the demo wallet">
        <span className="dot" /> <span className="mono">{shortAddr(DEMO_ADDRESS)}</span>
      </button>
    );
  }
  return (
    <>
      <button className={`btn btn-paper ${block ? "btn-block" : ""}`} onClick={() => setOpen(true)}>
        Connect
      </button>
      <Modal open={open} onClose={() => setOpen(false)}>
        <h3>Try it with a demo wallet</h3>
        <p className="note">
          This is a preview of $STRIKE before launch. The demo wallet holds a few options and 25M $STRIKE so you can buy, cash out,
          sell and claim. Nothing is signed and nothing is sent on-chain.
        </p>
        <div className="wallet-list">
          <button
            onClick={() => {
              connectDemo(true);
              setOpen(false);
            }}
          >
            <span className="wallet-demo" aria-hidden="true">
              <img src="/favicon.svg" alt="" />
            </span>
            Demo wallet
            <span className="pill call" style={{ marginLeft: "auto" }}>
              preview
            </span>
          </button>
        </div>
      </Modal>
    </>
  );
}

function WalletConnect({ block }: { block: boolean }) {
  const conn = useConnection();
  const connectors = useConnectors();
  const connect = useConnect();
  const disconnect = useDisconnect();
  const switchChain = useSwitchChain();
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState<string>();

  if (conn.isConnected && conn.chainId !== robinhood.id) {
    return (
      <button className={`btn btn-put ${block ? "btn-block" : ""}`} onClick={() => switchChain.mutate({ chainId: robinhood.id })}>
        Switch to Robinhood Chain
      </button>
    );
  }
  if (conn.isConnected) {
    return (
      <button className={`btn ${block ? "btn-block" : ""}`} onClick={() => disconnect.mutate()} title="Disconnect">
        <span className="dot" /> <span className="mono">{shortAddr(conn.address)}</span>
      </button>
    );
  }

  // Prefer EIP-6963 wallets (they have a name and icon); fall back to generic injected.
  const wallets = connectors.filter((c) => c.type === "injected" && c.id !== "injected");
  const list = wallets.length ? wallets : connectors;

  return (
    <>
      <button className={`btn btn-paper ${block ? "btn-block" : ""}`} onClick={() => setOpen(true)}>
        Connect
      </button>
      <Modal open={open} onClose={() => setOpen(false)}>
        <h3>Connect a wallet</h3>
        <p className="note">$STRIKE runs on Robinhood Chain. Any EVM wallet works.</p>
        <div className="wallet-list">
          {list.map((c) => (
            <button
              key={c.uid}
              onClick={() =>
                connect.mutate(
                  { connector: c, chainId: robinhood.id },
                  { onSuccess: () => setOpen(false), onError: (e) => setErr(friendlyError(e)) },
                )
              }
            >
              {c.icon ? <img src={c.icon} alt="" /> : <span className="dot" />}
              {c.id === "injected" ? "Browser wallet" : c.name}
            </button>
          ))}
          {list.length === 0 && <p className="note">No wallet found. Install MetaMask, Rabby or any EVM wallet.</p>}
        </div>
        {err && <p className="note warn" style={{ marginTop: 12 }}>{err}</p>}
      </Modal>
    </>
  );
}
